import React, { useEffect, useState } from "react"
import { Bar, Pie } from "react-chartjs-2"
import { useTranslation } from "react-i18next"
import { Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { formatWithCommas } from "common/utils"
import Select from "react-select"

const SalesByProducts = ({ marketingDashboardData, getTotal, barOptions }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [geographicalSales, setGeographicalSalesData] = useState({
    labels: [],
    datasets: [],
  })
  const [revenueByProduct, setRevenueByProduct] = useState({
    labels: [],
    datasets: [],
  })
  const [revenueByCategory, setRevenueByCategory] = useState({
    labels: [],
    datasets: [],
  })
  const [newVsReturning, setNewVsReturning] = useState({
    labels: [],
    datasets: [],
  })
  const [bestSellingProductsData, setBestSellingProductsData] = useState({
    labels: [],
    datasets: [],
  })
  const [selectedProduct, setSelectedProduct] = useState(null)

  // Custom options to show quantity in tooltip alongside revenue
  const barOptionsWithQuantity = {
    ...barOptions,
    plugins: {
      ...barOptions?.plugins,
      tooltip: {
        ...(barOptions?.plugins?.tooltip || {}),
        callbacks: {
          ...(barOptions?.plugins?.tooltip?.callbacks || {}),
          label: context => {
            const revenue = context?.raw || 0
            const quantity = context?.dataset?.quantities?.[context?.dataIndex]
            const revenueText = `${t("Revenue")}: ${formatWithCommas(revenue)}`
            if (typeof quantity === "number") {
              return `${revenueText}  |  ${t("Quantity")}: ${formatWithCommas(
                quantity
              )}`
            }
            return revenueText
          },
        },
      },
    },
  }

  useEffect(() => {
    // Geographical Sales by City
    if (marketingDashboardData?.geographicalSalesByCity?.length) {
      const labels = marketingDashboardData.geographicalSalesByCity.map(
        item => (language === "en" ? item?.name : item?.nameAr) || t("Unknown")
      )
      const totalAmount = marketingDashboardData.geographicalSalesByCity.map(
        item => item?.totalAmount || 0
      )

      setGeographicalSalesData({
        labels,
        datasets: [
          {
            label: t("Total Amount"),
            data: totalAmount,
            backgroundColor: "#4C6FFF",
          },
        ],
      })
    } else {
      setGeographicalSalesData({ labels: [], datasets: [] })
    }

    // Revenue by Product
    if (marketingDashboardData?.revenueByProduct?.length) {
      const labels = marketingDashboardData.revenueByProduct.map(item =>
        language === "en" ? item?.name : item?.nameAr
      )
      const revenue = marketingDashboardData.revenueByProduct.map(
        item => item?.revenue || 0
      )
      const quantities = marketingDashboardData.revenueByProduct.map(
        item => item?.quantity || 0
      )

      setRevenueByProduct({
        labels,
        datasets: [
          {
            label: t("Revenue"),
            data: revenue,
            quantities,
            backgroundColor: "#00E396",
          },
        ],
      })
    } else {
      setRevenueByProduct({ labels: [], datasets: [] })
    }

    // Revenue by Category
    if (marketingDashboardData?.revenueByCategory?.length) {
      const labels = marketingDashboardData.revenueByCategory.map(item =>
        language === "en" ? item?.name : item?.nameAr
      )
      const revenue = marketingDashboardData.revenueByCategory.map(
        item => item?.revenue || 0
      )
      const quantities = marketingDashboardData.revenueByCategory.map(
        item => item?.quantity || 0
      )

      setRevenueByCategory({
        labels,
        datasets: [
          {
            label: t("Revenue"),
            data: revenue,
            quantities,
            backgroundColor: "#FEB019",
          },
        ],
      })
    } else {
      setRevenueByCategory({ labels: [], datasets: [] })
    }

    // New vs Returning Customers
    if (marketingDashboardData?.newVsCustomers) {
      const labels = [t("New Customers"), t("Returning Customers")]
      const data = [
        marketingDashboardData?.newVsCustomers?.newCustomers || 0,
        marketingDashboardData?.newVsCustomers?.returningCustomers || 0,
      ]

      setNewVsReturning({
        labels,
        datasets: [
          {
            label: t("Customers"),
            data,
            backgroundColor: "#775DD0",
          },
        ],
      })
    } else {
      setNewVsReturning({ labels: [], datasets: [] })
    }

    // Best Selling Products (Pie)
    if (marketingDashboardData?.bestSellingProducts?.length) {
      const labels = marketingDashboardData.bestSellingProducts.map(item =>
        language === "en" ? item?.name : item?.nameAr
      )
      let quantities = marketingDashboardData.bestSellingProducts.map(
        item => item?.quantity || 0
      )

      setBestSellingProductsData({
        labels,
        datasets: [
          {
            label: t(""),
            data: quantities,
            backgroundColor: [
              "#4C6FFF",
              "#00E396",
              "#FEB019",
              "#FF4560",
              "#775DD0",
              "#2E93FA",
            ],
          },
        ],
      })
    } else {
      setBestSellingProductsData({ labels: [], datasets: [] })
    }
  }, [marketingDashboardData, language, t])

  return (
    <>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <CardTitle>{t("Geographical Sales Breakdown")}</CardTitle>
              <Bar
                width={550}
                height={180}
                data={geographicalSales}
                options={barOptions}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <CardTitle>{t("Revenue by product")}</CardTitle>
              <Bar
                width={550}
                height={180}
                data={revenueByProduct}
                options={barOptionsWithQuantity}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3">
                <CardTitle>{t("Sales Summary By Product")}</CardTitle>
                {marketingDashboardData?.revenueByProduct?.length > 0 && (
                  <Select
                    styles={{ width: "100px" }}
                    value={selectedProduct}
                    isClearable
                    placeholder={t("Filter By Product")}
                    options={marketingDashboardData?.revenueByProduct?.map(
                      item => ({
                        value: item.itemId,
                        label: language === "en" ? item.name : item.nameAr,
                      })
                    )}
                    onChange={option => setSelectedProduct(option)}
                  />
                )}
              </div>
              {marketingDashboardData?.revenueByProduct?.length === 0 ? (
                <p className="text-center my-3">{t("No data found")}</p>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead className="table-light">
                      <tr>
                        <th>{t("Item")}</th>
                        <th>{t("Quantity")}</th>
                        <th>{t("Amount")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketingDashboardData?.revenueByProduct
                        ?.filter(item => {
                          if (!selectedProduct) return true
                          if (item.itemId === selectedProduct?.value)
                            return true
                          return false
                        })
                        ?.map((item, key) => {
                          return (
                            <tr key={key}>
                              <td>
                                {language === "en" ? item?.name : item?.nameAr}
                              </td>
                              <td>{formatWithCommas(item?.quantity)}</td>
                              <td>
                                {formatWithCommas(item?.revenue?.toFixed(2))}
                              </td>
                            </tr>
                          )
                        })}
                    </tbody>
                    {!selectedProduct && (
                      <tfoot className="table-light">
                        <tr>
                          <th>{t("Total")}</th>

                          <th>
                            {formatWithCommas(
                              getTotal(
                                marketingDashboardData?.revenueByProduct,
                                "quantity"
                              )
                            )}
                          </th>
                          <th>
                            {formatWithCommas(
                              getTotal(
                                marketingDashboardData?.revenueByProduct,
                                "revenue"
                              )?.toFixed(2)
                            )}
                          </th>
                        </tr>
                      </tfoot>
                    )}
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <CardTitle>{t("Revenue by category")}</CardTitle>
              <Bar
                width={550}
                height={180}
                data={revenueByCategory}
                options={barOptionsWithQuantity}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={6}>
          <Card>
            <CardBody>
              <CardTitle>{t("New vs Returning customer")}</CardTitle>
              <div style={{ height: 300, position: "relative" }}>
                <Bar
                  data={newVsReturning}
                  options={{
                    ...barOptions,
                    responsive: true,
                    maintainAspectRatio: false,
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <CardBody>
              <CardTitle>{t("Best Selling Products")}</CardTitle>
              <div style={{ height: 300, position: "relative" }}>
                <Pie
                  data={bestSellingProductsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "right",
                      },
                    },
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default SalesByProducts
